import { useQuery } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch, useSelector } from "react-redux";

import { permissionsData, selectionsData } from "@/data";
import {
    getBatchNamesSelectionListQuery,
    getClientXmlFileTreeSelectionListQuery,
    getCountriesSelectionListQuery,
    getDistributionFileTypesSelectionListQuery,
    getDistributionPreOrderTypesSelectionListQuery,
    getDocumentTypesSelectionListQuery,
    getGenresSelectionListQuery,
    getImageTypesSelectionListQuery,
    getReleaseTypesSelectionListQuery,
    getRevenueReportsSystemFieldsSelectionListQuery,
    getRightTypesSelectionListQuery,
    getRolesSelectionListQuery,
    getTrackFormatsSelectionListQuery,
    getTrackTypesSelectionListQuery,
    getVideoTypesSelectionListQuery,
    getXmlFileTypesSelectionListQuery,
} from "@/graph";
import { setSelection } from "@/redux/actions";
import { canDo } from "@/utils";

const {
    batchNames: batchNamesKey,
    countries: countriesKey,
    distributionFileTypes: distributionFileTypesKey,
    documentTypes: documentTypesKey,
    genres: genresKey,
    imageTypes: imageTypesKey,
    releaseTypes: releaseTypesKey,
    revenueReportsSystemFields: revenueReportsSystemFieldsKey,
    rightTypes: rightTypesKey,
    roles: rolesKey,
    trackFormats: trackFormatsKey,
    trackTypes: trackTypesKey,
    videoTypes: videoTypesKey,
    xmlFileTree: xmlFileTreeKey,
    xmlFileTypes: xmlFileTypesKey,
} = selectionsData.keys;

export const useCountriesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getCountriesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[countriesKey],
    });

    if (data?.countries && !selections[countriesKey]) {
        dispatch(
            setSelection({
                [countriesKey]: data.countries.map(({ en_name: name, id, territoryCode: code }) => ({
                    code,
                    label: name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useGenresSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getGenresSelectionListQuery, {
        skip: !isAuthenticated || !!selections[genresKey],
    });

    if (data?.genres && !selections[genresKey]) {
        dispatch(
            setSelection({
                [genresKey]: data.genres.map(({ id, name }) => ({
                    label: name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useRolesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated, user } = useAuth0();

    const { accountRead: accountReadPermission } = permissionsData.permissions.keys;

    const { data, loading } = useQuery(getRolesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[rolesKey] || !canDo(user, [accountReadPermission]),
    });

    if (data?.listRoles && !selections[rolesKey]) {
        const roleKeys = data.listRoles.reduce(
            (acc, value) => ({
                ...acc,
                [value.name]: value.name,
            }),
            {},
        );

        const roleIds = data.listRoles.reduce(
            (acc, value) => ({
                ...acc,
                [value.id]: value.name,
            }),
            {},
        );

        dispatch(
            setSelection({
                [rolesKey]: {
                    ids: roleIds,
                    keys: roleKeys,
                    list: data.listRoles.map(({ id, name }) => ({
                        label: name,
                        value: id,
                    })),
                },
            }),
        );
    }

    return { loading };
};

export const useTrackTypesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getTrackTypesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[trackTypesKey],
    });

    if (data?.trackTypes && !selections[trackTypesKey]) {
        dispatch(
            setSelection({
                [trackTypesKey]: data.trackTypes.map(({ id, name }) => ({
                    label: name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useTrackFormatsSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getTrackFormatsSelectionListQuery, {
        skip: !isAuthenticated || !!selections[trackFormatsKey],
    });

    if (data?.trackFormats && !selections[trackFormatsKey]) {
        dispatch(
            setSelection({
                [trackFormatsKey]: data.trackFormats.map(({ displayName, id }) => ({
                    label: displayName,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useDistributionFileTypesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getDistributionFileTypesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[distributionFileTypesKey],
    });

    if (data?.distributeTypes && !selections[distributionFileTypesKey]) {
        dispatch(
            setSelection({
                [distributionFileTypesKey]: data.distributeTypes.map(({ id, name }) => ({
                    label: name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useDistributionPreOrderTypesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getDistributionPreOrderTypesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[selectionsData.keys.preOrderTypes],
    });

    if (data?.getPreOrderAllowedTypes && !selections[selectionsData.keys.preOrderTypes]) {
        dispatch(
            setSelection({
                [selectionsData.keys.preOrderTypes]: data.getPreOrderAllowedTypes.map(({ id, name }) => ({
                    label: name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useDocumentTypesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getDocumentTypesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[documentTypesKey],
    });

    if (data?.documentTypes && !selections[documentTypesKey]) {
        dispatch(
            setSelection({
                [documentTypesKey]: data.documentTypes.map(({ id, name, name_ar: nameAr }) => ({
                    label: `${name}-${nameAr}`,
                    name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useImageTypesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getImageTypesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[imageTypesKey],
    });

    if (data?.imageTypes && !selections[imageTypesKey]) {
        dispatch(
            setSelection({
                [imageTypesKey]: data.imageTypes.map(({ id, name }) => ({
                    label: name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useReleaseTypesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getReleaseTypesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[releaseTypesKey],
    });

    if (data?.releaseTypes && !selections[releaseTypesKey]) {
        dispatch(
            setSelection({
                [releaseTypesKey]: data.releaseTypes.map(({ id, name }) => ({
                    label: name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useRightTypesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getRightTypesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[rightTypesKey],
    });

    if (data?.rightTypes && !selections[rightTypesKey]) {
        dispatch(
            setSelection({
                [rightTypesKey]: data.rightTypes.map(({ id, name }) => ({
                    label: name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useVideoTypesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getVideoTypesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[videoTypesKey],
    });

    if (data?.videoTypes && !selections[videoTypesKey]) {
        dispatch(
            setSelection({
                [videoTypesKey]: data.videoTypes.map(({ id, name }) => ({
                    label: name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useXmlFileTypesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getXmlFileTypesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[xmlFileTypesKey],
    });

    if (data?.downloadXmlTypes && !selections[xmlFileTypesKey]) {
        dispatch(
            setSelection({
                [xmlFileTypesKey]: data.downloadXmlTypes.map(({ id, name }) => ({
                    label: name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useBatchNamesSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getBatchNamesSelectionListQuery, {
        skip: !isAuthenticated || !!selections[batchNamesKey],
    });

    if (data?.distributeBatchNames && !selections[batchNamesKey]) {
        dispatch(
            setSelection({
                [batchNamesKey]: data.distributeBatchNames.map(({ id, name }) => ({
                    label: name,
                    value: id,
                })),
            }),
        );
    }

    return { loading };
};

export const useClientXmlFileTreeSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getClientXmlFileTreeSelectionListQuery, {
        skip: !isAuthenticated || !!selections[xmlFileTreeKey],
    });

    if (data?.getSelectionsMenuOptions && !selections[xmlFileTreeKey]) {
        dispatch(
            setSelection({
                [xmlFileTreeKey]: data.getSelectionsMenuOptions.selections,
            }),
        );
    }

    return { loading };
};

export const useRevenueReportsSystemFieldsSelectionList = () => {
    const selections = useSelector((state) => state.selectionsReducer);

    const dispatch = useDispatch();

    const { isAuthenticated } = useAuth0();

    const { data, loading } = useQuery(getRevenueReportsSystemFieldsSelectionListQuery, {
        skip: !isAuthenticated || !!selections[revenueReportsSystemFieldsKey],
    });

    if (data?.getRevenueMappingTemplate && !selections[revenueReportsSystemFieldsKey]) {
        dispatch(
            setSelection({
                [revenueReportsSystemFieldsKey]: data.getRevenueMappingTemplate.posts.map(({ value }) => ({
                    label: value,
                    value,
                })),
            }),
        );
    }

    return { loading };
};
